export interface ApiClientOptions {
  baseUrl: string;
  getToken: () => Promise<string>;
}

export class ApiError extends Error {
  public override readonly name = 'ApiError';
  public readonly status: number;
  public readonly code: string;
  public readonly body: unknown;

  constructor(status: number, code: string, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

// A file the server answered with, kept as bytes: the caller decides how it reaches the person.
// `filename` is `null` when the server names none OR when the header is not readable — cross-origin,
// `Content-Disposition` is only visible if the server lists it in `Access-Control-Expose-Headers`.
export interface DownloadedFile {
  blob: Blob;
  filename: string | null;
}

// RFC 6266: `filename*` (percent-encoded UTF-8) wins over the plain `filename` when both are sent.
function filenameFrom(header: string | null): string | null {
  if (header === null) return null;
  const extended = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (extended?.[1] !== undefined) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // A malformed escape falls through to the plain form.
    }
  }
  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/.exec(header);
  const value = plain?.[2] ?? plain?.[1];
  return value === undefined ? null : value.trim() || null;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly getToken: () => Promise<string>;

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, '');
    this.getToken = options.getToken;
  }

  async get<T>(path: string): Promise<T> {
    return this.request<T>('GET', path);
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body);
  }

  // Partial update of a resource. The one panel route that needs it is «Editar datos» on a
  // «Usuario» (CBX-11384): app-service chose `PATCH` over `POST …/update` deliberately — editing
  // three fields of an account is a partial update of the representation, not an act executed on
  // it like `…/delete` or `…/collect`, which carry a verb precisely because they have no
  // representation to patch (app-service ADR-0047).
  async patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PATCH', path, body);
  }

  async delete<T>(path: string): Promise<T> {
    return this.request<T>('DELETE', path);
  }

  // A download through the same token path as every read: the Authorization header, never a token in
  // the URL — which is why a file is fetched here and not handed to the browser as a bare link.
  async getFile(path: string): Promise<DownloadedFile> {
    const token = await this.getToken();
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      throw ApiClient.failure('GET', path, response, await response.text());
    }

    return {
      blob: await response.blob(),
      filename: filenameFrom(response.headers.get('content-disposition')),
    };
  }

  private static failure(method: string, path: string, response: Response, text: string): ApiError {
    const isJson = response.headers.get('content-type')?.includes('application/json') ?? false;
    let payload: unknown = text === '' ? undefined : text;
    if (isJson && text !== '') {
      try {
        payload = JSON.parse(text) as unknown;
      } catch {
        payload = text;
      }
    }
    const code =
      typeof payload === 'object' &&
      payload !== null &&
      'code' in payload &&
      typeof (payload as { code: unknown }).code === 'string'
        ? (payload as { code: string }).code
        : `HTTP_${response.status.toString()}`;
    return new ApiError(response.status, code, payload, `${method} ${path} failed: ${code}`);
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = await this.getToken();
    const response = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

    const isJson = response.headers.get('content-type')?.includes('application/json') ?? false;
    // Read the body once as text: an empty 2xx body is valid HTTP and must not crash
    // `JSON.parse('')`. Empty → undefined; otherwise parse JSON or keep the raw text.
    const text = await response.text();
    const payload: unknown = text === '' ? undefined : isJson ? (JSON.parse(text) as unknown) : text;

    if (!response.ok) {
      const code = isJson && typeof payload === 'object' && payload !== null && 'code' in payload && typeof (payload as { code: unknown }).code === 'string'
        ? (payload as { code: string }).code
        : `HTTP_${response.status.toString()}`;
      throw new ApiError(response.status, code, payload, `${method} ${path} failed: ${code}`);
    }

    return payload as T;
  }
}
